import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, HStack, Image, Badge, Text, VStack, Spacer, Divider
} from '@chakra-ui/react';
import { Form, Field } from 'react-final-form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';

import axios from 'axios';
import { useCookies } from 'react-cookie';

import { FormattedDate } from "react-intl"

import { apiLoadACompany, apiUpdateACompany } from '../../../redux/apis';
import { clearCompanyActionStatus } from '../../../redux/actions';
import { getACompany, getCompanyActionStatus, getCompanyActionMsg, getCompanyError } from '../../../redux/selectors';
import { SUCCESS_STATUS, FAILED_STATUS, ERROR_CODES } from '../../../constants';

import Header from '../Header';
import Navbar from '../Navbar';
import DropZoneWithImage from '../../../components/DropZoneWithImage';
import { displayConfirmAlert } from '../../utils';

function ClinicInformation({ companyDetail, actionStatus, actionMsg, error, apiLoadACompany, apiUpdateACompany, clearCompanyActionStatus }) {
    const [ logoFile, setLogoFile ] = useState(null);
    const [ isAction, setIsAction ] = useState(false);
    const [ isProcessing, setIsProcessing ] = useState(false);

    const mounted = useRef();
    const navigate = useNavigate();
    const [ cookies, setCookie, removeCookie ] = useCookies(["loginCookie", "selectedClinic"]);

    useEffect(() => {
        if(!mounted.current) {
            apiLoadACompany({ id: cookies?.loginCookie?.data?.companyDetail?.id });
            setIsAction(cookies.loginCookie?.data?.roles === "admin");
            mounted.current = true;
        }
    });

    useEffect(() => {
        if(actionStatus === SUCCESS_STATUS) {
            displayConfirmAlert(actionMsg, () => {
                clearCompanyActionStatus();
                removeCookie("loginCookie", { path: "/" });
                removeCookie("selectedClinic");
                navigate("/login");
            })
        } 
        else if(actionStatus === FAILED_STATUS) {         
            toast.error(error, {
                position: "bottom-right"
            });
            clearCompanyActionStatus();
        }
    }, [actionStatus])

    const validate = (values) => {
        const errors = {};

        if(!values.name) {
            errors.name = "Required";
        }
        if(!values.owner) {
            errors.owner = "Required";
        }
        if(!values.address) {
            errors.address = "Required";
        }
        if(!values.phone) {
            errors.phone = "Required";
        }
        if(!values.email) {
            errors.email = "Required";
        }

        return errors;
    }

    const onSubmit = async(values) => {
        setIsProcessing(true);

        try
        {
            let logo_url = null;

            if(logoFile) {
                // upload logo
                let logoFiledata = new FormData();
                logoFiledata.append('uri', logoFile);

                let resUpload = await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/upload?subPath=companies`, logoFiledata, { 
                    headers: { "Content-Type": "multipart/form-data" },
                    auth: {
                        username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                        password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                    }
                });

                if(resUpload.data?.status === 200) {
                    logo_url = resUpload.data.fileName;
                }
            }

            apiUpdateACompany({ 
                id: companyDetail.id, 
                payload: {
                    ...values, 
                    logo_url: logo_url || companyDetail?.logo_url
                } 
            });

            setIsProcessing(false);
        }
        catch(error) {
            if(error?.code) {
                toast.error(`${ERROR_CODES[error?.code]} (${error?.code})`, {
                    position: "bottom-right"
                });
            }
        }
    }

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{
                ...companyDetail,
            }}
            validate={validate}
            render={({ handleSubmit, form, submitting, pristine, valid, values }) => {
                let registerTypeBadge = null;
        
                if(companyDetail?.register_type === "FREE") {
                    registerTypeBadge = <Badge variant="solid" p={1}>MIỄN PHÍ</Badge>
                } else if(companyDetail?.register_type === "BASIC") {
                    registerTypeBadge = <Badge variant="solid" colorScheme='blue' p={1}>CƠ BẢN</Badge>
                } else if(companyDetail?.register_type === "PRO") {
                    registerTypeBadge = <Badge variant="solid" colorScheme='red' p={1}>CHUYÊN NGHIỆP</Badge>
                } 

                return (
                    <form onSubmit={handleSubmit}>
                        <Box>
                            <Header />
                            <Navbar />

                            <Flex justify="center" my={4}>
                                <VStack borderRadius={20} boxShadow="lg" bg="white.100" width={{base: '100%', md: '50%', lg: '50%'}} px={8} py={4}>
                                    <Heading my="10px" size="lg">Thông tin phòng khám</Heading>

                                    <Divider />
                                    
                                    <Field name="name">
                                        {({ input, meta }) => (
                                            <FormControl isRequired>
                                                <FormLabel>Tên phòng khám:</FormLabel>
                                                <Input {...input} type='text' placeholder='Nhập Tên phòng khám' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="owner">
                                        {({ input, meta }) => (
                                            <FormControl isRequired>
                                                <FormLabel>Người đại diện:</FormLabel>
                                                <Input {...input} type='tel' placeholder='Nhập Người đại diện' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="address">
                                        {({ input, meta }) => (
                                            <FormControl isRequired>
                                                <FormLabel>Địa chỉ:</FormLabel>
                                                <Input {...input} type='tel' placeholder='Nhập Địa chỉ' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="phone">
                                        {({ input, meta }) => (
                                            <FormControl isRequired>
                                                <FormLabel>Số điện thoại:</FormLabel>
                                                <Input {...input} type='tel' placeholder='Nhập Số điện thoại' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="email">
                                        {({ input, meta }) => (
                                            <FormControl isRequired>
                                                <FormLabel>Email:</FormLabel>
                                                <Input {...input} type='email' placeholder='Nhập Email' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <FormControl mt={2}>
                                        <FormLabel>Logo:</FormLabel>
                                        <HStack spacing={2}>
                                            {
                                                companyDetail?.logo_url && !logoFile &&
                                                <Image alt="logo" src={`${process.env.REACT_APP_UPLOADS_ENDPOINT_URL}/companies/${companyDetail?.logo_url}`} 
                                                    width="100px" height="100px"
                                                />
                                            }
                                            <DropZoneWithImage callback={setLogoFile}/>
                                        </HStack>
                                    </FormControl>

                                    <Field name="alarm_phone">
                                        {({ input, meta }) => (
                                            <FormControl>
                                                <FormLabel>Số điện thoại cấp cứu:</FormLabel>
                                                <Input {...input} type='text' placeholder='Nhập Số điện thoại cấp cứu' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="work_time">
                                        {({ input, meta }) => (
                                            <FormControl>
                                                <FormLabel>Giờ làm việc:</FormLabel>
                                                <Input {...input} type='text' placeholder='Nhập Giờ làm việc' />
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Flex w="100%" align="center" justify="flex-start" gap={2}>
                                        <Text>Đang sử dụng gói</Text>
                                        {registerTypeBadge}
                                    </Flex>

                                    <Flex w="100%" align="center" justify="flex-start" gap={2}>
                                        <Text>Ngày hết hạn:</Text>
                                        <Text color="red">
                                            <FormattedDate value={companyDetail?.active_expired}/>
                                        </Text>
                                        <Button colorScheme='orange' mt={2} isDisabled={!isAction}
                                                onClick={
                                                    () => {
                                                        window.open(`/pricing/extend/${companyDetail.id}`);
                                                    }
                                                }
                                            >
                                            Gia hạn phòng khám
                                        </Button>
                                    </Flex>
                                    {/* <FormControl>
                                        <Flex direction={{ base: "column", md: "row", lg: "row" }} align="center" justify="flex-start">
                                            <FormLabel>Ngày hết hạn:
                                                <span style={{ color: "red"}}><FormattedDate value={companyDetail && companyDetail?.active_expired}/> </span>
                                            </FormLabel>
                                            <Badge>Miễn Phí</Badge>
                                            <Button colorScheme='orange' mt={2} isDisabled={!isAction}
                                                onClick={
                                                    () => {
                                                        window.open(`/pricing/extend/${companyDetail.id}`);
                                                    }
                                                }
                                            >
                                                Gia hạn phòng khám
                                            </Button>
                                        </Flex>
                                    </FormControl> */}

                                    <Button colorScheme='green' type='submit' size="lg" 
                                        isLoading={isProcessing}
                                        loadingText="Đang cập nhật"
                                        isDisabled={!valid || !isAction} mt={4}
                                    >
                                        Cập nhật thông tin
                                    </Button>
                                </VStack>
                                <ToastContainer />
                            </Flex>
                        </Box>
                    </form>
                )
            }}
        />
    )
}

const mapStateToProps = (state) => {
    const companyDetail =          getACompany(state);
    const actionStatus  =          getCompanyActionStatus(state);
    const actionMsg     =          getCompanyActionMsg(state);
    const error         =          getCompanyError(state);

    return { companyDetail, actionStatus, actionMsg, error };
}

const mapDispatchToProps = (dispatch) => ({
    apiLoadACompany:                    payload => dispatch(apiLoadACompany(payload)),
    apiUpdateACompany:                  payload => dispatch(apiUpdateACompany(payload)),
    clearCompanyActionStatus:           payload => dispatch(clearCompanyActionStatus(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ClinicInformation);